import { useState } from 'react';
import { updateOrganization } from '../../../../api/remiApi';

interface UseUpdateOnboardingDataProps {
  organizationId: string | undefined;
}

export const useUpdateOnboardingData = ({ organizationId }: UseUpdateOnboardingDataProps) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateMLSName = async (mlsEntries: any[], index: number, newName: string, currentOnboardingData?: any) => {
    if (!organizationId) {
      setError('Organization ID is required');
      return false;
    }

    setSaving(true);
    setError(null);

    try {
      const updatedMlsEntries = [...mlsEntries];
      updatedMlsEntries[index] = {
        ...updatedMlsEntries[index],
        mls_name: newName,
      };

      const updatedOnboardingData = {
        ...(currentOnboardingData ?? {}),
        idx: {
          ...(currentOnboardingData?.idx ?? {}),
          mls_entries: updatedMlsEntries,
        },
      };

      await updateOrganization(organizationId, {
        onboardingData: JSON.stringify(updatedOnboardingData),
      });

      return true;
    } catch (err) {
      console.error('Failed to update MLS name:', err);
      setError('Failed to save changes');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const removeMLSEntry = async (mlsEntries: any[], index: number, currentOnboardingData?: any) => {
    if (!organizationId) {
      setError('Organization ID is required');
      return false;
    }

    setSaving(true);
    setError(null);

    try {
      const updatedMlsEntries = mlsEntries.filter((_, i) => i !== index);

      const updatedOnboardingData = {
        ...(currentOnboardingData ?? {}),
        idx: {
          ...(currentOnboardingData?.idx ?? {}),
          mls_entries: updatedMlsEntries,
        },
      };

      await updateOrganization(organizationId, {
        onboardingData: JSON.stringify(updatedOnboardingData),
      });

      return true;
    } catch (err) {
      console.error('Failed to remove MLS entry:', err);
      setError('Failed to save changes');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const updateWebsiteCompleted = async (completed: boolean, currentOnboardingData?: any) => {
    if (!organizationId) {
      setError('Organization ID is required');
      return false;
    }

    setSaving(true);
    setError(null);

    try {
      // Drop the legacy camelCase key (written by old Firestore onboarding)
      // so canonical snake_case is the only form on the row going forward.
      const { websiteCompleted: _legacy, ...existingWebsite } =
        (currentOnboardingData?.website ?? {}) as Record<string, unknown>;

      const updatedOnboardingData = {
        ...(currentOnboardingData ?? {}),
        website: {
          ...existingWebsite,
          website_completed: completed,
        },
      };

      await updateOrganization(organizationId, {
        onboardingData: JSON.stringify(updatedOnboardingData),
      });

      return true;
    } catch (err) {
      console.error('Failed to update website completed status:', err);
      setError('Failed to save changes');
      return false;
    } finally {
      setSaving(false);
    }
  };

  return {
    updateMLSName,
    removeMLSEntry,
    updateWebsiteCompleted,
    saving,
    error,
  };
};
